import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Field, Form, Formik } from 'formik';
import * as yup from 'yup';
import clsx from 'clsx';
import { Loader2, Receipt } from 'lucide-react';
import TextField from 'Containers/Common/TextField';
import { getActiveSalon } from 'Containers/Auth/store/selectors';
import { authActions } from 'Containers/Auth/store';
import useWindowSize from 'utils/useWindowSize';
import {
  buildStripeCustomerPayload,
  getBillingInitialValues,
  getBillingValidationSchema,
} from '../../utils/billingFormUtils';
import styles from './BillingSetup.module.scss';

const fieldProps = {
  component: TextField,
  height: '50px',
  margin: '0 0 12px 0',
};

const scrollToFirstError = () => {
  const errorNode = document.querySelector('[class*="BillingSetup"] [class*="error"]');
  errorNode?.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

const ProvinceField = ({ field, form, ...props }) => (
  <TextField
    {...props}
    field={{
      ...field,
      onChange: (event) => {
        const next = String(event.target.value || '')
          .replace(/[^A-Za-z]/g, '')
          .toUpperCase()
          .slice(0, 2);
        form.setFieldValue(field.name, next);
      },
    }}
    form={form}
  />
);

const BillingSetupForm = ({ onSuccess, isMobile }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const activeSalon = useSelector(getActiveSalon());
  const [isSaving, setIsSaving] = useState(false);

  return (
    <Formik
      initialValues={getBillingInitialValues(activeSalon)}
      validationSchema={yup.object().shape(getBillingValidationSchema())}
      enableReinitialize
      onSubmit={(values) => {
        if (isSaving) return;
        setIsSaving(true);
        const safetyTimer = window.setTimeout(() => setIsSaving(false), 20000);
        dispatch(
          authActions.CREATE_CUSTOMER.REQUEST(
            { customer: buildStripeCustomerPayload(values) },
            () => {
              window.clearTimeout(safetyTimer);
              setIsSaving(false);
              onSuccess?.();
            },
          ),
        );
      }}
    >
      {({ submitCount, isValid }) => (
        <BillingSetupFields
          isMobile={isMobile}
          isSaving={isSaving}
          submitCount={submitCount}
          isValid={isValid}
          t={t}
        />
      )}
    </Formik>
  );
};

const BillingSetupFields = ({ isMobile, isSaving, submitCount, isValid, t }) => {
  useEffect(() => {
    if (submitCount > 0 && !isValid) {
      scrollToFirstError();
    }
  }, [submitCount, isValid]);

  return (
    <Form className={styles.form} noValidate>
      <div className={styles.form_grid}>
        <Field
          {...fieldProps}
          name='nomeSalone'
          id='nomeSalone'
          autoComplete='organization'
          label={t('home.billing.legalName.label')}
          placeholder={t('home.billing.legalName.placeholder')}
        />
        <Field
          {...fieldProps}
          name='phone'
          id='phone'
          type='tel'
          inputMode='tel'
          autoComplete='tel'
          label={t('home.billing.phone.label')}
          placeholder={t('home.billing.phone.placeholder')}
        />
        <Field
          {...fieldProps}
          name='pec'
          id='pec'
          type='email'
          autoComplete='email'
          label={t('home.billing.pec.label')}
          placeholder={t('home.billing.pec.placeholder')}
        />
        <Field
          {...fieldProps}
          name='pIva'
          id='pIva'
          inputMode='numeric'
          autoComplete='off'
          label={t('home.billing.vat.label')}
          placeholder={t('home.billing.vat.placeholder')}
        />

        <div className={styles.section_label}>
          <span>{t('home.billing.address.title')}</span>
          <span className={styles.country_chip}>{t('settings.payments.billingSetup.countryFixed')}</span>
        </div>

        <div className={styles.hidden_field}>
          <Field type='hidden' name='country' />
        </div>

        <div className={styles.address_row}>
          <div className={styles.field_full}>
            <Field
              {...fieldProps}
              name='line1'
              id='line1'
              autoComplete='address-line1'
              label={t('home.billing.address.street.label')}
              placeholder={t('home.billing.address.street.placeholder')}
            />
          </div>
          <Field
            {...fieldProps}
            name='line2'
            id='line2'
            autoComplete='address-line2'
            label={t('home.billing.address.streetNumber.label')}
            placeholder={t('home.billing.address.streetNumber.placeholder')}
          />
          <Field
            {...fieldProps}
            name='postal_code'
            id='postal_code'
            inputMode='numeric'
            autoComplete='postal-code'
            label={t('home.billing.address.postalCode.label')}
            placeholder={t('home.billing.address.postalCode.placeholder')}
          />
        </div>

        <div className={styles.city_row}>
          <Field
            {...fieldProps}
            name='city'
            id='city'
            autoComplete='address-level2'
            label={t('home.billing.address.city.label')}
            placeholder={t('home.billing.address.city.placeholder')}
          />
          <Field
            {...fieldProps}
            component={ProvinceField}
            name='state'
            id='state'
            autoComplete='address-level1'
            maxLength={2}
            label={t('home.billing.address.state.label')}
            placeholder={t('home.billing.address.state.placeholder')}
          />
        </div>
      </div>

      <div className={clsx(styles.actions, { [styles.actions_sticky]: isMobile })}>
        <button type="submit" className={styles.submit_button} disabled={isSaving}>
          {isSaving ? (
            <>
              <Loader2 size={18} strokeWidth={2.2} className={styles.submit_spinner} />
              {t('settings.payments.billingSetup.saving')}
            </>
          ) : (
            t('settings.payments.billingSetup.submit')
          )}
        </button>
      </div>
    </Form>
  );
};

export const BillingSetup = ({ onSuccess }) => {
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();

  return (
    <section className={clsx(styles.section, { [styles.section_mobile]: isMobile })}>
      <div className={styles.card}>
        <div className={styles.card_header}>
          <span className={styles.card_icon} aria-hidden="true">
            <Receipt size={18} strokeWidth={2.2} />
          </span>
          <div className={styles.card_header_copy}>
            <h4 className={styles.section_title}>{t('settings.payments.billingSetup.title')}</h4>
            <p className={styles.section_description}>
              {t('settings.payments.billingSetup.description')}
            </p>
          </div>
        </div>

        <BillingSetupForm onSuccess={onSuccess} isMobile={isMobile} />
      </div>
    </section>
  );
};
